import React from 'react'

import { Elements } from '@stripe/react-stripe-js'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { DEFAULT_THEME_COLORS, getBodyFonts, getCaptionFonts } from 'css/theme'

import { useMaybeStripe } from 'utils/functions/getStripePromise'
import TextPlaceloader from 'modules/ui/atoms/Loaders/TextPlaceloader'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import { convertUSDToCents } from 'utils/currency'
import useOrder from 'modules/order/state/order/use-order'
import { inputStyles } from 'css/utils'

type StripeInlineCreditCardInputsProps = {
  onCardBrand?: (brand: string) => void
  onLoadingChange?: (isLoading: boolean) => void
}

// Define the ref type to match the original stripe-checkout component
export type StripeInlineCreditCardInputsRef = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  savePaymentMethodToStore: () => Promise<any>
  focusCard: () => void
}

const StripeInlineCreditCardInputs = React.forwardRef<
  StripeInlineCreditCardInputsRef,
  StripeInlineCreditCardInputsProps
>(({ onCardBrand, onLoadingChange }, ref) => {
  const options = _useOptions()
  const stripe = useMaybeStripe()

  return (
    <Elements options={options} stripe={stripe}>
      <StripeInlineCreditCardForm
        onCardBrand={onCardBrand}
        onLoadingChange={onLoadingChange}
        ref={ref}
      />
    </Elements>
  )
})

StripeInlineCreditCardInputs.displayName = 'StripeInlineCreditCardInputs'

// The actual form component that handles Stripe Elements
const StripeInlineCreditCardForm = React.forwardRef<
  StripeInlineCreditCardInputsRef,
  StripeInlineCreditCardInputsProps
>(({ onCardBrand, onLoadingChange }, ref) => {
  const order = useOrder()
  const elements = useElements()
  const stripe = useStripe()

  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    // https://stripe.com/docs/js/element/events
    const element = elements?.getElement('payment')

    element?.on('ready', () => {
      element.focus()
      setIsLoading(false)
      onLoadingChange?.(false)
    })
  }, [elements, onLoadingChange])

  // Notify parent when loading state changes
  React.useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  React.useImperativeHandle(ref, () => ({
    savePaymentMethodToStore: async () => {
      if (!elements || !stripe) {
        throw new Error('The payment method elements are not defined.')
      }

      // Note: This validates the form and throws an error if something is wrong.
      // However, it shouldn't be possible for a user to actually trigger this method if
      // there are still errors on the form, but calling it is still required by Stripe
      await elements.submit()

      // Create the PaymentMethod using the details collected by the Payment Element
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            name: `${order.contact.value.firstName} ${order.contact.value.lastName}`,
            email: order.contact.value.email,
            phone: order.contact.value.phone,
            address: {
              city: order.address.value.city,
              // TODO: Stripe expects a two letter country code instead of the full name
              country: order.address.value.country,
              line1: order.address.value.line1,
              line2: order.address.value.line2 ?? '',
              postal_code: order.address.value.zipCode,
              state: order.address.value.state,
            },
          },
        },
      })

      if (error) return error

      if (paymentMethod) {
        order.paymentDetails.setMethod(paymentMethod)

        if (paymentMethod.card?.brand && onCardBrand) {
          onCardBrand(paymentMethod.card.brand)
        }
      }
    },
    focusCard: () => elements?.getElement('payment')?.focus(),
  }))

  return (
    <>
      {isLoading && (
        <Column>
          <Row style={{ marginBottom: '0.5rem' }}>
            <Column>
              <TextPlaceloader w={85} fs="14px" lh="20px" />
              <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
            </Column>
          </Row>

          <Row style={{ marginBottom: '0.5rem' }}>
            <Column style={{ width: '100%' }}>
              <Row>
                <Column style={{ flexGrow: 0.5 }}>
                  <TextPlaceloader w={68} fs="14px" lh="20px" />
                  <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
                </Column>

                <Column style={{ paddingLeft: '0.5rem', flexGrow: 0.5 }}>
                  <TextPlaceloader w={32} fs="14px" lh="20px" />
                  <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
                </Column>
              </Row>
            </Column>
          </Row>
        </Column>
      )}

      <PaymentElement
        id="payment-element"
        options={{
          layout: 'tabs',
          fields: { billingDetails: 'never' },
          terms: { card: 'never' },
          wallets: {
            applePay: 'never',
            googlePay: 'never',
          },
        }}
      />
    </>
  )
})

StripeInlineCreditCardForm.displayName = 'InlineCreditCardForm'

// Stripe options configuration (copied from original stripe-checkout.tsx)
const _useOptions = () => {
  const order = useOrder()

  const amountInCents = convertUSDToCents(order.dcf.value.totalAmount)

  // Ensure amount is at least 1 cent to avoid Stripe error
  const validAmount = Math.max(amountInCents, 1)

  const appearance = {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: 'rgb(27, 25, 24)', // DEFAULT_THEME_COLORS['--theme-color--neutral-10'], but... rgba is not supported
      colorDanger: inputStyles.colorDanger,
      borderRadius: inputStyles.borderRadius,
    },
    rules: {
      '.Input': {
        lineHeight: '1.5rem',
        border: inputStyles.border,
        padding: 'var(--p-spacing2) var(--p-spacing3)',
      },
      '.Input--invalid': {
        color: 'inherit',
        boxShadow: 'none',
        backgroundColor: '#FEF6F6',
        borderColor: 'var(--colorDanger)',
      },
      '.Label': {
        '-webkit-font-smoothing': 'initial',
        color: DEFAULT_THEME_COLORS['--theme-color--neutral-30'],
        ...getBodyFonts('sm'),
      },
      '.Label--invalid': {
        color: 'var(--colorDanger)',
      },
      '.Error': {
        color: 'var(--colorDanger)',
        ...getCaptionFonts(),
      },
      '.Input:focus': {
        outline: '2px dotted #2972FF',
        outlineOffset: '1px',
      },
    },
  }

  const options = {
    appearance,
    currency: 'usd',
    amount: validAmount,
    loader: 'never' as const,
    mode: 'payment' as const,
    payment_method_types: ['card'],
    paymentMethodCreation: 'manual' as const,
  }

  return options
}

export default StripeInlineCreditCardInputs
